import { FC, useRef, useState } from 'react';

import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';

import { useMutation } from '@redwoodjs/web';

import { showErrorToast } from 'src/components/Toasts/toast';
import {
    ABOUT_BUSINESS_INITIAL_VALUES,
    OUTLET_INITIAL_VALUES,
} from 'src/constants/formInitialValues.ts/addMerchantStep3';
import { addMerchantValidation } from 'src/constants/formValidations/addMerchantStep3';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { UPDATE_MERCHANT_APPLICATION_DOCUMENTS_MUTATION } from 'src/graphql/mutations/updateApplicationDoucments.mutation';
import { useValidateVAT } from 'src/hooks';
import useCountryHelpers from 'src/hooks/useCountryHelpers';
import { MerchantInformation_FormValues } from 'src/types/AddMerchant/merchantInfo';
import { updatePrincipalDocuments } from 'src/utils/utils';

import { AboutBusinessForm } from '../../components/AboutBusinessForm';
import { AddressInformation } from '../../components/AddressInformation';

import { PrincipalComponent, principalInitialValue } from './components/Principal';

const MerchantInformation: FC = () => {
    const { getCountryName } = useCountryHelpers();
    const bussinessFormRef = useRef(null);
    const addressInformationRef = useRef(null);
    const principalRef = useRef(null);
    const [isNext, setIsNext] = useState(false);
    const { addMerchant, merchantApplication, handleBack } = useAddMerchantContext();
    const [updateDocumentsMutation] = useMutation(UPDATE_MERCHANT_APPLICATION_DOCUMENTS_MUTATION);
    const { onValidateVAT } = useValidateVAT();

    const onSubmit = async (input) => {
        if (
            input?.principals?.length > 0 &&
            merchantApplication?.supportingDocuments?.principalDocuments?.length > 0
        ) {
            const { principals } = input;
            const updatedSupportingDocuments = updatePrincipalDocuments(
                principals,
                merchantApplication
            );

            await updateDocumentsMutation({
                variables: {
                    input: {
                        applicationId: merchantApplication.id,
                        supportingDocuments: {
                            ...updatedSupportingDocuments,
                        },
                    },
                },
            });
        }

        await addMerchant({
            merchantInformation: { ...input, isStepCompleted: true },
        });
    };

    function handleScrollOnErrors(errors) {
        const scrollOptions = { behavior: 'smooth', block: 'center' }; // Define scroll options

        const hasPrincipalErrors =
            Array.isArray(errors.principal) &&
            errors.principal.some((principal) => {
                return (
                    principal &&
                    (principal.firstName ||
                        principal.lastName ||
                        principal.nationality ||
                        principal.email ||
                        principal.position)
                );
            });

        if (
            errors?.aboutBusiness?.businessType ||
            errors?.aboutBusiness?.legalCompanyName ||
            errors?.aboutBusiness?.registeredNumber ||
            errors?.aboutBusiness?.tradingName ||
            errors?.aboutBusiness?.vat?.vatNumber
        ) {
            bussinessFormRef.current.scrollIntoView(scrollOptions);
        } else if (
            errors?.outlets?.[0]?.tradingAddress?.eircode ||
            errors?.outlets?.[0]?.tradingAddress?.county ||
            errors?.outlets?.[0]?.tradingAddress?.addressLine1 ||
            errors?.outlets?.[0]?.tradingAddress?.townOrCity ||
            errors?.aboutBusiness?.registeredAddress?.eircode ||
            errors?.aboutBusiness?.registeredAddress?.addressLine1 ||
            errors?.aboutBusiness?.registeredAddress?.townOrCity ||
            errors?.aboutBusiness?.registeredAddress?.county
        ) {
            addressInformationRef.current.scrollIntoView(scrollOptions);
        } else if (hasPrincipalErrors) {
            principalRef.current.scrollIntoView(scrollOptions);
        }
    }

    const initialValues = {
        aboutBusiness: {
            ...ABOUT_BUSINESS_INITIAL_VALUES,
            registeredAddress: {
                country: getCountryName(merchantApplication?.partner?.countryDetails?.alpha2Code),
            },
        },
        outlets: [
            {
                ...OUTLET_INITIAL_VALUES,
                tradingAddress: {
                    country: getCountryName(
                        merchantApplication?.partner?.countryDetails?.alpha2Code
                    ),
                },
            },
        ],
        sameAsRegisteredAddress: false,
        numberOfOutlets: merchantApplication?.merchantInformation?.numberOfOutlets ?? 1,
        principals: [
            {
                ...principalInitialValue,
                addresses: [
                    {
                        ...principalInitialValue.addresses?.[0],
                        country: getCountryName(
                            merchantApplication?.partner?.countryDetails?.alpha2Code
                        ),
                    },
                ],
            },
        ],
        isStepCompleted: false,
        ...(merchantApplication?.merchantInformation ?? ({} as MerchantInformation_FormValues)),
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={addMerchantValidation(
                merchantApplication?.serviceRequirements?.service?.serviceType
            )}
            validateOnChange
            enableReinitialize
            onSubmit={onSubmit}
        >
            {(formikProps) => {
                const { errors, values, setFieldError, setFieldTouched, validateForm, setErrors } =
                    formikProps;

                const handleSubmit = async (e) => {
                    e.preventDefault();

                    const hasBillingContact = values.principals?.some((principal) => {
                        return principal.isBillingContact === true;
                    });
                    const handleErrorToast = () => {
                        if (Object.keys(errors).length > 0) {
                            const errorMessage = 'Please fill in required fields.';
                            showErrorToast(errorMessage);
                        }
                    };
                    if (!hasBillingContact && values.principals.length > 1) {
                        showErrorToast('Please select Billing Contact');
                        return;
                    }
                    if (values.aboutBusiness.vat.vatRegistered) {
                        return await onValidateVAT(
                            values.aboutBusiness.vat.vatNumber,
                            async () => {
                                setFieldTouched('aboutBusiness.vat.vatNumber', true);
                                handleScrollOnErrors(errors);
                                const validationErrors = await validateForm();

                                const updatedErrors = {
                                    ...validationErrors,
                                    aboutBusiness: {
                                        vat: {
                                            vatNumber: 'Enter a valid VAT number',
                                        },
                                    },
                                };
                                setErrors(updatedErrors);
                            },
                            () => {
                                setFieldError('aboutBusiness.vat.vatNumber', null);
                                formikProps.handleSubmit(e);
                                handleErrorToast();
                            }
                        );
                    }
                    formikProps.handleSubmit(e);
                    handleErrorToast();
                };

                return (
                    <Form
                        style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
                        onSubmit={handleSubmit}
                    >
                        <Box ref={bussinessFormRef}>
                            <AboutBusinessForm {...formikProps} />
                        </Box>
                        <Box ref={addressInformationRef}>
                            <AddressInformation {...formikProps} />
                        </Box>
                        <Box ref={principalRef}>
                            <PrincipalComponent formik={formikProps} isNext={isNext} />
                        </Box>
                        {/* Submit Buttons */}
                        <Box display="flex" justifyContent="flex-end">
                            <Button
                                sx={{
                                    alignSelf: 'end',
                                    marginTop: '24px',
                                    marginRight: '22px',
                                }}
                                onClick={handleBack}
                                variant="back-button"
                            >
                                Back
                            </Button>

                            <Button
                                sx={{
                                    alignSelf: 'end',
                                    marginTop: '24px',
                                }}
                                variant="main"
                                type="submit"
                                onClick={() => {
                                    handleScrollOnErrors(errors);

                                    setIsNext(!isNext);
                                }}
                            >
                                Next
                            </Button>
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default MerchantInformation;
